package er.prateek.crossly.ads.entitlement

import kotlinx.coroutines.flow.StateFlow

/**
 * What the user has paid for. Today nothing sells this, so it is always [NONE] -- but every
 * ad decision in the app already routes through it, so turning on a paid tier later is a
 * matter of binding a real [PurchaseGateway] in `AppContainer` and touching nothing else.
 */
data class Entitlement(
    val adFree: Boolean = false,
    val source: EntitlementSource = EntitlementSource.NONE,
    /**
     * `null` for a non-expiring entitlement (lifetime purchase, or none at all). A
     * subscription carries its period end here so a cached entitlement can lapse offline
     * instead of granting ad-free forever after one successful check.
     */
    val expiresAtEpochMs: Long? = null,
) {
    fun isActiveAt(nowEpochMs: Long): Boolean =
        adFree && (expiresAtEpochMs == null || expiresAtEpochMs > nowEpochMs)

    companion object {
        val NONE = Entitlement()
    }
}

enum class EntitlementSource {
    NONE,

    /** Granted out of band -- support credit, promo code, review copy. */
    PROMOTIONAL,
    SUBSCRIPTION,
    LIFETIME_PURCHASE,
}

/** Observable ad-free state. The single question the ads layer ever asks about payment. */
interface EntitlementProvider {
    val entitlement: StateFlow<Entitlement>

    /** Re-checks the source of truth (store receipts, server) and updates [entitlement]. */
    suspend fun refresh()
}

/**
 * The billing seam. A Play Billing / StoreKit / Stripe implementation drops in here; the
 * default [NoPurchaseGateway] reports that nothing is for sale, which is what keeps the
 * "Remove ads" UI hidden until there is genuinely something to buy.
 */
interface PurchaseGateway {
    /** Empty while no paid tier is live. */
    suspend fun products(): List<AdFreeProduct>

    /** Current entitlement per the store. Must be safe to call offline. */
    suspend fun queryEntitlement(): Entitlement

    suspend fun purchase(productId: String): PurchaseResult

    /** "Restore purchases" -- required by App Store review for any non-consumable. */
    suspend fun restore(): Entitlement
}

data class AdFreeProduct(
    val id: String,
    val title: String,
    val description: String,
    /** Pre-formatted in the store's own locale and currency -- never format prices yourself. */
    val formattedPrice: String,
    val recurring: Boolean,
)

sealed interface PurchaseResult {
    data class Success(val entitlement: Entitlement) : PurchaseResult
    data object Cancelled : PurchaseResult
    data class Failed(val message: String) : PurchaseResult

    /** No billing implementation bound on this platform -- the current state everywhere. */
    data object Unavailable : PurchaseResult
}

object NoPurchaseGateway : PurchaseGateway {
    override suspend fun products(): List<AdFreeProduct> = emptyList()
    override suspend fun queryEntitlement(): Entitlement = Entitlement.NONE
    override suspend fun purchase(productId: String): PurchaseResult = PurchaseResult.Unavailable
    override suspend fun restore(): Entitlement = Entitlement.NONE
}
